/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import MenuIcon from "@material-ui/icons/Menu";
import "./navbar.scss";
import {
  AdminMenu,
  ClientMenu,
  getEmployeeNavMenu,
  getManagerNavMenu,
} from "./contants";
import {
  resolveClientActiveMenuId,
  useClientNavTrail,
} from "../../hooks/useClientNavTrail";
import { getUserRole } from "../../utils/common";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";

const CLIENT_BOTTOM_MENU_IDS = new Set([
  "our_services",
  "contact_us",
  "my_tax_team",
]);


const Sidebar = ({ collapsed = false, onHamburgerClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const unreadCount = useSelector((state) => state.message?.unreadSummary?.unread_count || 0);
  const userRole = getUserRole();
  const isClient = userRole === "client";
  const { categoryTab } = useClientCategoryTab();

  const NavMenu =
    userRole === "admin"
      ? AdminMenu
      : userRole === "employee"
        ? getEmployeeNavMenu()
        : userRole === "manager"
          ? getManagerNavMenu()
          : ClientMenu.filter(
              (item) =>
                item.id !== "businesses" || categoryTab === "business",
            );

  const topMenu = isClient
    ? NavMenu?.filter((item) => !CLIENT_BOTTOM_MENU_IDS.has(item.id))
    : NavMenu?.filter((item) => !["employees", "reports", "HRMS", "timesheet_dashboard"].includes(item.id));
  const bottomMenu = isClient
    ? NavMenu?.filter((item) => CLIENT_BOTTOM_MENU_IDS.has(item.id))
    : NavMenu?.filter((item) => ["employees", "reports", "HRMS", "timesheet_dashboard"].includes(item.id));

  const activeClientMenuId = useMemo(() => {
    if (!isClient) return null;
    return resolveClientActiveMenuId(location.pathname, ClientMenu);
  }, [isClient, location.pathname]);

  const navTrail = useClientNavTrail(activeClientMenuId, isClient);

  const getTrailClass = (menuId) => {
    if (!isClient || menuId === activeClientMenuId) return "";
    if (navTrail.recent1 === menuId) return "nav-recent-1";
    if (navTrail.recent2 === menuId) return "nav-recent-2";
    return "";
  };

  // Custom isActive function to handle special cases
  const isMenuActive = (menu, isActive) => {
    // For "client" menu item, also check if we're on client_details page
    if (menu.id === "client") {
      const currentPath = location.pathname;
      // Do NOT treat /client_transfer as /client
      if (currentPath.includes(`/finlo/${userRole}/client_transfer`)) {
        return isActive;
      }
      // Check if path starts with /finlo/:role/client (includes /client and /client_details)
      return isActive || currentPath.startsWith(`/finlo/${userRole}/client`);
    }

    // For "timesheet_dashboard" menu item, also check if we're on create_timesheet page
    if (menu.id === "timesheet_dashboard") {
      const currentPath = location.pathname;
      // Check if path is create_timesheet
      return isActive || currentPath === `/finlo/${userRole}/create_timesheet`;
    }

    return isActive;
  };

  const renderMenuItem = (menu) => {
    const isMessagesMenu = menu.id === "messages";
    const showUnreadBadge = isClient && isMessagesMenu && unreadCount > 0;

    return (
      <NavLink
        key={menu.item}
        to={menu.path}
        data-tour={`nav-${menu.id}`}
        className={({ isActive }) => {
          const active = isMenuActive(menu, isActive);
          const trailClass = active ? "" : getTrailClass(menu.id);
          return `finlo-navlink ${active ? "active" : ""} ${trailClass}`.trim();
        }}
        title={menu.item}
        onClick={(e) => {
          e.preventDefault();
          navigate(menu.path);
        }}
      >
        <li className="finlo-navitem">
          <span className="icon-wrap">
            <img
              src={menu.icon}
              className={`${
                userRole === "admin" ? "admin-nav-icon" : "client-nav-icon"
              }`}
              alt=""
              aria-hidden="true"
            />
            {showUnreadBadge && collapsed && (
              <span className="navbar-unread-badge-dot" />
            )}
          </span>
          <span className="finlo-navlabel">{menu.item}</span>
          {showUnreadBadge && !collapsed && (
            <span className="navbar-unread-badge">{unreadCount}</span>
          )}
        </li>
      </NavLink>
    );
  };

  return (
    <aside
      className={`finlo-navbar ${collapsed ? "collapsed" : "expanded"} ${
        isClient ? "is-client" : ""
      }`}
      role="navigation"
      aria-label="Sidebar"
    >
      <div className="navbar-toggle-container">
        <button
          type="button"
          className="navbar-hamburger-btn"
          onClick={onHamburgerClick}
          aria-label="Toggle sidebar"
        >
          <MenuIcon />
        </button>
      </div>

      <ul className="finlo-navlist top-navlist">
        {topMenu?.map(renderMenuItem)}
      </ul>
      {bottomMenu && bottomMenu.length > 0 && (
        <>
          <hr className="navbar-divider" />
          <ul className="finlo-navlist bottom-navlist">
            {bottomMenu?.map(renderMenuItem)}
          </ul>
        </>
      )}


    </aside>
  );
};

export default Sidebar;
